import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
  FormControl,
  FormLabel,
  CircularProgress,
  Backdrop,
  IconButton,
} from '@mui/material'
import { makeStyles } from '../../utils/hooks/makeStyles'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import { toast } from 'react-toastify'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  UploadPdfToBucketApi,
  UploadPftScreeningApi,
  GetPftScreeningApi,
} from '../../services/CampManagementService'
import { getBucketFile } from '../../services/constants'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PFTScreeningProps {
  appointmentData?: any
}

function PFTScreening({ appointmentData }: PFTScreeningProps) {
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [spdfUrl, setSpdfUrl] = useState<string>('')
  const [abnormality, setAbnormality] = useState<string | null>(null)
  const [description, setDescription] = useState<string>('')
  const [predictedFev1, setPredictedFev1] = useState<string>('')
  const [fev1Fvc, setFev1Fvc] = useState<string>('')
  const [touchedFev1, setTouchedFev1] = useState(false)
  const [touchedFev1Fvc, setTouchedFev1Fvc] = useState(false)
  const [loading, setLoading] = useState(false)
  const [tricogData, setTricogData] = useState<any>({})
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)

  const useStyles = makeStyles((theme) => ({
    root: {
      width: '100%',
      padding: theme.spacing(2),
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
      position: 'relative',
    },
    backdrop: {
      color: '#fff',
      zIndex: theme.zIndex.drawer + 1,
      position: 'absolute',
    },
    fileBox: {
      marginBottom: theme.spacing(3),
    },
    hiddenInput: {
      display: 'none',
    },
    selectedText: {
      marginTop: theme.spacing(1),
      color: theme.palette.success.main,
    },
    pdfViewer: {
      marginBottom: theme.spacing(3),
      border: '1px solid #ccc',
      overflow: 'auto',
      textAlign: 'center',
      width: '100%',
      height: 900,
    },
    pdfLoading: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(3),
    },
    pdfToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      borderBottom: '1px solid #ccc',
      backgroundColor: '#f5f5f5',
      position: 'relative',
    },
    zoomLabel: {
      minWidth: 48,
      textAlign: 'center',
    },
    openInNewBtn: {
      position: 'absolute',
      right: 8,
    },
    pdfPager: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(2),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      borderTop: '1px solid #ccc',
      backgroundColor: '#f5f5f5',
    },
    inputsRow: {
      display: 'flex',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    formControl: {
      marginBottom: theme.spacing(3),
    },
    descriptionField: {
      marginBottom: theme.spacing(3),
    },
  }))

  const classes = useStyles()

  // Fetch existing PFT screening data
  const fetchPftScreening = async () => {
    if (
      appointmentData?.campId &&
      appointmentData?.uhid &&
      appointmentData?.visitId
    ) {
      try {
        const response = await GetPftScreeningApi(
          appointmentData.campId,
          appointmentData.uhid,
          appointmentData.visitId
        )
        if (response?.success) {
          if (response?.data?.pftDocumentUrl) {
            setTricogData(response.data || {})
            try {
              const fileData: any = await getBucketFile(
                response?.data?.pftDocumentUrl,
                'PFT File'
              )
              setAbnormality(response?.data?.status || '')
              setDescription(response?.data?.interpretation || '')
              setPredictedFev1(response?.data?.predictedFev1 || '')
              setFev1Fvc(response?.data?.fev1Fvc || '')
              setPdfUrl(fileData?.pdfFileUrl)
              setSpdfUrl(fileData?.pdfFileUrl) // Fetch the file to get the correct name and URL
            } catch (error) {
              console.error('Error fetching PFT file:', error)
            }
          }
        }
      } catch (error: any) {
        console.error('Error fetching PFT screening:', error)
      }
    }
  }

  // Fetch when patient or appointment data changes
  useEffect(() => {
    fetchPftScreening()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData?.uhid, appointmentData?.campId, appointmentData?.visitId])

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file only')
      return
    }

    setPdfFile(file)
    const url = URL.createObjectURL(file)
    setPdfUrl(url)

    // Upload file immediately
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('patientUhid', appointmentData?.uhid || '')
      formData.append('campId', appointmentData?.campId || '')
      formData.append('visitId', appointmentData?.visitId || '')
      formData.append('publicDoc', 'true')

      const response = await UploadPdfToBucketApi(formData)

      if (response.success) {
        setSpdfUrl(response?.data?.url)
        setPdfFile(null) // Clear local file after successful upload
      } else {
        toast.error(response.message)
      }

      toast.success(response.message || 'File uploaded successfully')
    } catch (error: any) {
      console.error('PFT Upload Error:', error)
      toast.error(error?.message || 'Failed to upload file')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    // Validate that file has been uploaded to server
    if (!spdfUrl) {
      toast.error('Please wait for the file to upload before submitting')
      return
    }

    if (!predictedFev1) {
      toast.error('Predicted FEV1 is required')
      return
    }

    if (!fev1Fvc) {
      toast.error('FEV1/FVC is required')
      return
    }

    setLoading(true)
    try {
      const payload = {
        campId: appointmentData?.campId || '',
        patientUhid: appointmentData?.uhid || '',
        visitId: appointmentData?.visitId || '',
        documentUrl: spdfUrl, // Use only server URL, not local blob
        normal: null,
        status: abnormality,
        interpretation: description,
        predictedFev1: predictedFev1,
        fev1Fvc: fev1Fvc,
        doctorId: appointmentData?.visitingDoctorId,
        doctorName: appointmentData?.visitingDoctorName,
        patientName: appointmentData?.name,
      }

      const response = await UploadPftScreeningApi(payload)

      if (response.success) {
        toast.success('PFT report submitted successfully')
        // Reset form - keep pdfUrl to show the document
        setPdfFile(null)
        setAbnormality('')
        setDescription('')
        setPredictedFev1('')
        setFev1Fvc('')
        setTouchedFev1(false)
        setTouchedFev1Fvc(false)
        fetchPftScreening()
        setSpdfUrl('')
      } else {
        toast.error(response.message || 'Failed to submit report')
      }
    } catch (error: any) {
      console.error('Error submitting PFT report:', error)
      toast.error(error?.message || 'Failed to submit report')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box className={classes.root}>
      <Typography variant='h6' gutterBottom>
        PFT Screening Report Upload
      </Typography>

      <Paper elevation={3} className={classes.paper}>
        {/* Loading Overlay */}
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color='inherit' />
        </Backdrop>

        {/* File Upload */}
        <Box className={classes.fileBox}>
          <input
            accept='application/pdf'
            className={classes.hiddenInput}
            id='pft-file-upload'
            type='file'
            onChange={handleFileChange}
            disabled={tricogData?.pftDocumentUrl ? true : false}
          />
          <label htmlFor='pft-file-upload'>
            <Button
              variant='contained'
              component='span'
              startIcon={
                loading ? <CircularProgress size={20} /> : <CloudUploadIcon />
              }
              fullWidth
              disabled={tricogData?.pftDocumentUrl ? true : false}
            >
              {loading
                ? 'Uploading...'
                : pdfFile
                ? 'Change PDF File'
                : 'Upload PDF File'}
            </Button>
          </label>
          {pdfFile && (
            <Typography variant='body2' className={classes.selectedText}>
              Selected: {pdfFile.name}
            </Typography>
          )}
        </Box>

        {/* PDF Viewer */}
        {pdfUrl && (
          <Box className={classes.pdfViewer}>
            {/* Zoom toolbar */}
            <Box className={classes.pdfToolbar}>
              <IconButton
                size='small'
                onClick={() =>
                  setScale((s) => Math?.max(0.5, +(s - 0.25)?.toFixed(2)))
                }
                disabled={scale <= 0.5}
                title='Zoom out'
              >
                –
              </IconButton>
              <Typography variant='body2' className={classes.zoomLabel}>
                {Math?.round(scale * 100)}%
              </Typography>
              <IconButton
                size='small'
                onClick={() =>
                  setScale((s) => Math?.min(3, +(s + 0.25)?.toFixed(2)))
                }
                disabled={scale >= 3}
                title='Zoom in'
              >
                +
              </IconButton>
              <IconButton
                size='small'
                onClick={() => setScale(1.0)}
                title='Reset zoom'
                disabled={scale === 1.0}
              >
                ↺
              </IconButton>
              <IconButton
                size='medium'
                onClick={() => window.open(pdfUrl, '_blank')}
                title='Open in new window'
                className={classes.openInNewBtn}
              >
                <OpenInNewIcon fontSize='medium' />
              </IconButton>
            </Box>
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: total }) => {
                setNumPages(total)
                setPageNumber(1)
              }}
              onLoadError={(err) => console.error('PDF load error:', err)}
              loading={
                <Box className={classes.pdfLoading}>
                  <CircularProgress />
                </Box>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
            {numPages > 1 && (
              <Box className={classes.pdfPager}>
                <IconButton
                  size='small'
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                >
                  ‹
                </IconButton>
                <Typography variant='body2'>
                  Page {pageNumber} of {numPages}
                </Typography>
                <IconButton
                  size='small'
                  onClick={() =>
                    setPageNumber((p) => Math.min(numPages, p + 1))
                  }
                  disabled={pageNumber >= numPages}
                >
                  ›
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {/* Predicted FEV1 and FEV1/FVC */}
        <Box className={classes.inputsRow}>
          <TextField
            size='small'
            label='Predicted FEV1'
            fullWidth
            required
            value={predictedFev1}
            onChange={(e) => setPredictedFev1(e.target.value)}
            onBlur={() => setTouchedFev1(true)}
            placeholder='Enter Predicted FEV1...'
            error={touchedFev1 && !predictedFev1}
            helperText={
              touchedFev1 && !predictedFev1 ? 'Predicted FEV1 is required' : ''
            }
            disabled={tricogData?.pftDocumentUrl ? true : false}
          />
          <TextField
            size='small'
            label='FEV1/FVC'
            fullWidth
            required
            value={fev1Fvc}
            onChange={(e) => setFev1Fvc(e.target.value)}
            onBlur={() => setTouchedFev1Fvc(true)}
            placeholder='Enter FEV1/FVC...'
            error={touchedFev1Fvc && !fev1Fvc}
            helperText={
              touchedFev1Fvc && !fev1Fvc ? 'FEV1/FVC is required' : ''
            }
            disabled={tricogData?.pftDocumentUrl ? true : false}
          />
        </Box>

        {/* Abnormality Detection */}
        {tricogData?.pftDocumentUrl && (
          <FormControl
            disabled={true}
            component='fieldset'
            className={classes.formControl}
          >
            <FormLabel component='legend'>Abnormality Status</FormLabel>
            <RadioGroup
              row
              value={abnormality}
              onChange={(e) => setAbnormality(e.target.value)}
            >
              <FormControlLabel
                value='normal'
                control={<Radio />}
                label='Normal'
              />
              <FormControlLabel
                value='abnormal'
                control={<Radio />}
                label='Abnormal'
              />
            </RadioGroup>
          </FormControl>
        )}

        {/* Description */}
        {tricogData?.pftDocumentUrl && (
          <TextField
            label='Description'
            multiline
            disabled={true}
            rows={4}
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Enter detailed description or findings...'
            className={classes.descriptionField}
          />
        )}

        {/* Submit Button */}
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={tricogData?.pftDocumentUrl ? true : false}
          fullWidth
        >
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </Paper>
    </Box>
  )
}

export default PFTScreening
